import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { SURVEY_LIMITS, SURVEY_RATING_ANCHORS } from '@/constants/residents/corkBoardConstants';

interface Props {
  prompt: string;
  value: number | undefined;
  onChange: (value: number) => void;
}

export const RatingQuestionView: React.FC<Props> = ({ prompt, value, onChange }) => {
  const { colors } = useThemeColors();
  const values: number[] = [];
  for (let v = SURVEY_LIMITS.ratingMin; v <= SURVEY_LIMITS.ratingMax; v++) values.push(v);

  return (
    <View style={styles.root}>
      <Text style={[REDESIGN_TYPOGRAPHY.cardTitle, { color: colors.text.primary }]}>{prompt}</Text>

      <View style={styles.scaleRow}>
        {values.map((v) => {
          const selected = v === value;
          return (
            <Pressable
              key={v}
              onPress={() => onChange(v)}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              accessibilityLabel={`Rating ${v}`}
              style={({ pressed }) => [
                styles.bubble,
                {
                  backgroundColor: selected ? colors.newsfeed.tagActiveText : colors.card.bg,
                  borderColor: selected ? colors.newsfeed.tagActiveText : colors.card.border,
                  opacity: pressed ? 0.85 : 1,
                },
              ]}
            >
              <Text
                style={[
                  REDESIGN_TYPOGRAPHY.button,
                  { color: selected ? PRIMITIVE_COLORS.white : colors.text.primary },
                ]}
              >
                {v}
              </Text>
            </Pressable>
          );
        })}
      </View>

      <View style={styles.anchorRow}>
        <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.tertiary }]}>
          {SURVEY_RATING_ANCHORS.min}
        </Text>
        <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.tertiary }]}>
          {SURVEY_RATING_ANCHORS.max}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { gap: REDESIGN_SPACING.contentGap },
  scaleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 6,
  },
  bubble: {
    minWidth: 40,
    height: 40,
    paddingHorizontal: 8,
    borderRadius: 20,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  anchorRow: { flexDirection: 'row', justifyContent: 'space-between' },
});
